import React, { useEffect, useState } from "react";
import { PieChart, Pie, Sector, ResponsiveContainer, Cell } from "recharts";
import { CommonService } from "../../Service/CommonService";
import { InventoryApiService } from "../../Service/InventoryApiService";

const CapacityDetailsChart = () => {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const [capacityData, setCapacityDetails] = useState(undefined);

  const getCapacityDetailsByCompanyId = async () => {
    const companyId = CommonService.getCompanyId();
    try {
      const response =
        await InventoryApiService.fetchCapacityDetailsByCompanyId(companyId);
      console.log(response.data);
      setCapacityDetails(response.data);
    } catch (error) {
      console.log("Error occurred while api calling " + error);
    }
  };

  useEffect(() => {
    getCapacityDetailsByCompanyId();
  }, []);

  // Prepare capacityData for pie chart
  const pieData = [
    {
      name: "Utilized Capacity",
      value: capacityData?.utilizedCapacity,
      percentage: capacityData?.utilizedCapacityPercentage,
      color: "#525E9D",
    },
    {
      name: "Available Capacity",
      value: capacityData?.availableCapacity < 0 ? 0 : capacityData?.availableCapacity,
      percentage: capacityData?.availableCapacityPercentage < 0 ? 0 : capacityData?.availableCapacityPercentage,
      color: "#97AEB8",
    },
  ];

  // Custom active shape renderer
  const renderActiveShape = (props: any) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        {/* Center text showing total when no active segment */}
        <>
          {/* <text
            x={cx}
            y={cy - 10}
            // textAnchor="middle"
            fill="#333"
            fontSize="14"
            fontWeight="bold"
          >
            Total: {capacityData.totalCapacity}
          </text>
          <text x={cx} y={cy + 5}  fill="#999" fontSize="10">
            Capacity Units
          </text> */}
        </>

        {/* Active segment */}
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />

        {/* Outer ring for active segment */}
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />

        {/* Leader line */}
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />

        {/* Label text */}
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
          fontSize="12"
          fontWeight="bold"
        >
          {`${payload.name}: ${value}`}
        </text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
          fontSize="11"
        >
          {`(${(percent * 100).toFixed(2)}%)`}
        </text>
      </g>
    );
  };

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(undefined);
  };

  if (!capacityData) {
    return (
      <div className="capacity-chart-wrapper">
        <div className="card h-100 shadow-sm border-0">
          <div className="text-center p-4 d-flex align-items-center justify-content-center flex-column my-auto loader-wrapper">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3">Loading capacity details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="capacity-chart-wrapper">
      <div className="row pb-3">
        {/* Left Panel - Utilization Analytics */}
        <div className="col-md-6">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body">
              <h5 className="card-title mb-4">Utilization Analytics</h5>
              <div className="chart-wrapper">
                <div className="chart-inner">
              {/* Interactive Pie Chart Container */}
              <div style={{ height: "400px", position: "relative"}}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      activeIndex={activeIndex}
                      activeShape={renderActiveShape}
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={120}  
                      outerRadius={160} 
                      dataKey="value"
                      onMouseEnter={onPieEnter}
                      onMouseLeave={onPieLeave}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>

                {/* Static center label when no hover */}
                {/* {activeIndex === undefined && ( */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      className="fw-bold"
                      style={{ fontSize: "14px", color: "#333" }}
                    >
                      Total: {capacityData.totalCapacity}
                    </div>
                    <div style={{ fontSize: "10px", color: "#999" }}>
                      Capacity Units
                    </div>
                  </div>
                {/* )} */}
              </div>

              {/* Static Legend */}
              <div className="d-flex justify-content-center mt-3">
                {pieData.map((entry, index) => (
                  <div key={index} className="d-flex align-items-center me-4">
                    <div
                      className="me-2"
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: entry.color,
                        borderRadius: "2px",
                      }}
                    ></div>
                    <span style={{ fontSize: "14px", color: entry.color }}>
                      {entry.name}
                    </span>
                  </div>
                ))}
              </div>
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Summarized Details */}
        <div className="col-md-6">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body">
              <h5 className="card-title mb-4">Summarized Details</h5>

              {/* Company and Contracts Info */}
              <div className="row mb-4 right-wrapper">
                <div className="col-6">
                  <div className="content-wrapper">
                    <p className="content-title">No. of Contracts</p>
                    <div className="title-bottom-line"></div>
                    <div className="d-flex align-items-start gap-3">
                      <p className="content-text">
                        {capacityData.numberOfContract}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="content-wrapper">
                    <p className="content-title">Total Capacity</p>
                    <div className="title-bottom-line"></div>
                    <div className="d-flex align-items-start gap-3">
                      <p className="content-text">
                        {capacityData.totalCapacity}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="content-wrapper">
                    <p className="content-title">Utilized Capacity</p>
                    <div className="title-bottom-line"></div>
                    <div className="d-flex align-items-start gap-3">
                      <p className="content-text">
                        {capacityData.utilizedCapacity} | (
                        {capacityData.utilizedCapacityPercentage.toFixed(2)}%)
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="content-wrapper">
                    <p className="content-title">Available Capacity</p>
                    <div className="title-bottom-line"></div>
                    <div className="d-flex align-items-start gap-3">
                      <p className="content-text">
                        {capacityData.availableCapacity < 0 ? 0 : capacityData.availableCapacity} | (
                        {capacityData.availableCapacityPercentage < 0 ? 0 : capacityData.availableCapacityPercentage.toFixed(2)}%)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapacityDetailsChart;
